import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Package, Star } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";

const STATUS_COLORS = {
  pending: "bg-[#F5A623]/15 text-[#B67A10]",
  processing: "bg-[#F5A623]/15 text-[#B67A10]",
  shipped: "bg-indigo-500/15 text-indigo-700",
  confirmed: "bg-[var(--brand-primary)]/15 text-[var(--brand-primary)]",
  delivered: "bg-[var(--brand-primary)]/15 text-[var(--brand-primary)]",
  cancelled: "bg-[var(--brand-accent)]/15 text-[var(--brand-accent)]",
};

export default function Account() {
  const { user, ready, subscribe } = useAuth();
  const { currency } = useSettings();
  const [orders, setOrders] = useState([]);
  const [upgrading, setUpgrading] = useState(false);

  const handleSubscribe = async () => {
    setUpgrading(true);
    try {
      await subscribe();
      toast.success("You're now a subscriber — free delivery unlocked!");
    } catch {
      toast.error("Could not upgrade. Try again.");
    } finally {
      setUpgrading(false);
    }
  };

  useEffect(() => {
    if (user && user.role !== "admin") {
      api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
    }
  }, [user]);

  if (!ready) return <div className="py-24 text-center text-[#5C665D]">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin" || user.role === "manager") return <Navigate to="/admin" replace />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A231C] font-display">
        Hi, {user.name?.split(" ")[0]}
      </h1>
      <p className="text-[#5C665D] mt-2">Your account &amp; orders</p>

      {/* Subscription card */}
      <div data-testid="subscription-card" className={`mt-6 rounded-xl p-5 border ${user.is_subscriber ? "bg-[#F1F7F2] border-[var(--brand-primary)]/30" : "bg-white border-[#E8E4D9]"}`}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-display font-semibold text-[#1A231C] flex items-center gap-2">
              <Star className="w-4 h-4 text-[var(--brand-accent)]" /> {user.is_subscriber ? "Subscriber" : "Standard member"}
            </p>
            <p className="text-sm text-[#5C665D] mt-0.5">
              {user.is_subscriber
                ? "You enjoy free delivery and priority/fast shipping at checkout."
                : "Upgrade to unlock free delivery and priority shipping."}
            </p>
          </div>
          {!user.is_subscriber && (
            <button data-testid="subscribe-upgrade" onClick={handleSubscribe} disabled={upgrading} className="bg-[var(--brand-primary)] text-white px-5 py-2.5 rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors disabled:opacity-60">
              {upgrading ? "Upgrading..." : "Become a subscriber"}
            </button>
          )}
        </div>
      </div>

      <h2 className="text-lg font-display font-semibold text-[#1A231C] mt-8 mb-3">Order history</h2>

      {orders.length === 0 ? (
        <div className="bg-white border border-[#E8E4D9] rounded-xl p-12 text-center mt-8">
          <Package className="w-12 h-12 text-[#E8E4D9] mx-auto" strokeWidth={1.5} />
          <p className="text-[#5C665D] mt-4">No orders yet.</p>
          <Link to="/shop" className="inline-block mt-4 text-[var(--brand-primary)] font-medium">
            Start shopping →
          </Link>
        </div>
      ) : (
        <div className="space-y-4 mt-8">
          {orders.map((o) => (
            <div key={o.id} data-testid={`account-order-${o.id}`} className="bg-white border border-[#E8E4D9] rounded-xl p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#5C665D]">{new Date(o.created_at).toLocaleString()}</p>
                  <p className="font-display font-semibold text-[#1A231C] mt-1">{currency}{o.total}</p>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${STATUS_COLORS[o.status] || ""}`}>
                  {o.status}
                </span>
              </div>
              <div className="mt-3 text-sm text-[#5C665D]">
                {o.items.map((it) => `${it.name} × ${it.qty}`).join(", ")}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
